import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, PhoneCall } from "lucide-react";
import WorldMap from "@/components/WorldMap";

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  // Scroll-linked transforms only applied at desktop via CSS gate; on mobile
  // the map sits statically so it's always fully visible.
  const rawScale = useTransform(scrollYProgress, [0, 0.55, 1], [1, 0.94, 0.86]);
  const rawY = useTransform(scrollYProgress, [0, 1], [0, -22]);
  const rawOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.95, 0.75]);
  const scale = useSpring(rawScale, { stiffness: 90, damping: 24, mass: 0.6 });
  const y = useSpring(rawY, { stiffness: 90, damping: 24, mass: 0.6 });
  const opacity = useSpring(rawOpacity, { stiffness: 90, damping: 24, mass: 0.6 });

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pt-24 sm:pt-28 pb-14 sm:pb-20 lg:pb-24 lg:min-h-[92vh] overflow-hidden"
    >
      {/* Depth overlays */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0) 35%, rgba(0,0,0,0.25) 100%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
        {/* items-start on lg → map top-aligns with headline for a cohesive composition.
            On mobile the grid collapses to one column — copy first, map second, cleanly stacked. */}
        <div className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-6 lg:items-start">
          {/* LEFT: Copy */}
          <div className="lg:col-span-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="inline-flex items-center gap-2 rounded-full border border-[#D9F99D]/30 bg-[#D9F99D]/[0.06] px-3 py-1.5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.22em] sm:tracking-[0.24em] text-[#D9F99D]"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#D9F99D] zx-pulse" />
              Global Executive Search · Est. 2026
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: "easeOut" }}
              data-testid="hero-headline"
              className="font-display mt-4 sm:mt-5 text-[28px] sm:text-[40px] lg:text-[54px] xl:text-[58px] leading-[1.08] sm:leading-[1.06] lg:leading-[1.04] tracking-tight"
            >
              Hiring <span className="zx-hi">Top Talent</span><br />
              for Fast‑Growing<br />
              Teams in the<br />
              <span className="text-white">Global Financial</span><br />
              <span className="text-[#D9F99D]">Trading Space.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              data-testid="hero-subheadline"
              className="mt-5 sm:mt-6 max-w-xl text-sm sm:text-base lg:text-lg text-neutral-200 leading-relaxed"
            >
              We build elite teams across Trading, FinTech, AI, and Digital
              Assets — <span className="text-white font-medium">fast, precise, and globally aligned.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="mt-7 flex flex-wrap items-center gap-3 sm:gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full bg-[#D9F99D] hover:bg-[#E4FBB1] text-black text-[14px] sm:text-[15px] font-semibold px-6 sm:px-9 py-[13px] sm:py-[16px] transition-transform duration-300 hover:scale-[1.03] zx-cta-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
              >
                Hire Talent
                <ArrowUpRight
                  size={18}
                  aria-hidden="true"
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-[#D9F99D] bg-white/[0.02] hover:bg-[#D9F99D]/[0.06] text-white text-[14px] sm:text-[15px] font-semibold px-6 sm:px-9 py-[13px] sm:py-[16px] transition-all duration-300 hover:scale-[1.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9F99D]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
              >
                <PhoneCall size={16} aria-hidden="true" className="text-[#D9F99D]" />
                Book a Call
              </a>
            </motion.div>
          </div>

          {/* RIGHT: World-map visual — on lg the map sits slightly lower so it
              aligns visually with the main headline block (not the eyebrow),
              creating an About-style balanced composition. Mobile unchanged. */}
          <div className="lg:col-span-6 relative lg:pt-14 xl:pt-16">
            {/* Desktop: scroll-linked micro-animation. Mobile: static, no transforms. */}
            <motion.div
              className="hidden lg:block relative mx-auto w-full max-w-[720px] lg:-ml-4 xl:-ml-10"
              style={{ scale, y, opacity, transformOrigin: "center top", willChange: "transform" }}
              data-testid="hero-map-wrap"
            >
              <WorldMap />
            </motion.div>
            <div
              className="block lg:hidden relative mx-auto w-full max-w-[540px]"
              data-testid="hero-map-wrap-mobile"
            >
              <WorldMap />
            </div>
          </div>
        </div>

        {/* Live ticker — pushed to bottom of hero so it anchors the composition */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="mt-10 sm:mt-12 lg:mt-14 relative zx-edge-fade overflow-hidden"
          aria-hidden
        >
          <div className="flex gap-10 zx-ticker whitespace-nowrap font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-neutral-500">
            {[
              "NYSE ▲ 1.24%",
              "FTSE ▼ 0.31%",
              "DXY 104.82",
              "BTC/USD 68,420",
              "ETH/USD 3,512",
              "EUR/USD 1.0842",
              "USD/JPY 154.02",
              "GOLD 2,398",
              "HSI ▲ 0.92%",
            ]
              .concat([
                "NYSE ▲ 1.24%",
                "FTSE ▼ 0.31%",
                "DXY 104.82",
                "BTC/USD 68,420",
                "ETH/USD 3,512",
                "EUR/USD 1.0842",
                "USD/JPY 154.02",
                "GOLD 2,398",
                "HSI ▲ 0.92%",
              ])
              .map((t, i) => (
                <span key={i} className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[#D9F99D]/70" />
                  {t}
                </span>
              ))}
          </div>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-10 sm:mt-12 lg:mt-14 relative"
          data-testid="hero-trust-strip"
        >
          <div className="flex items-center gap-3 sm:gap-6">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-white/0" />
            <span className="font-mono text-[9.5px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.28em] text-neutral-200 text-center">
              Trusted by Trading Firms, Brokers &amp; FinTech Companies Worldwide
            </span>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/10 to-white/0" />
          </div>
          <div
            data-testid="hero-trust-statement"
            className="mt-4 flex items-center justify-center gap-3 sm:gap-5 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.16em] sm:tracking-[0.22em] text-neutral-300 flex-wrap"
          >
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[#D9F99D]/80" aria-hidden="true" />
              Trading · FinTech · AI · Digital Assets
            </span>
            <span className="hidden sm:inline text-neutral-700">·</span>
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[#93C5FD]/80" aria-hidden="true" />
              28+ Global Markets
            </span>
            <span className="hidden sm:inline text-neutral-700">·</span>
            <span className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-[#C4B5FD]/80" aria-hidden="true" />
              C-Level &amp; Executive Search
            </span>
          </div>
        </motion.div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 sm:h-32"
        style={{
          background:
            "linear-gradient(180deg, transparent, rgba(11,11,11,0.95) 75%, #0B0B0B)",
        }}
      />
    </section>
  );
}
